/**
 * AccessDenied Component
 * Displays a friendly access denied message for users without creator permissions
 * or when accessing resources that don't belong to them
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './AccessDenied.css';

type DenialReason = 'role' | 'ownership' | 'unknown';

interface AccessDeniedProps {
  requiredRole?: 'admin';
  title?: string;
  message?: string;
  reason?: DenialReason;
}

const AccessDenied: React.FC<AccessDeniedProps> = ({
  requiredRole = 'admin',
  title,
  message,
  reason = 'unknown',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();

  const roleLabels: Record<string, { name: string; nameEn: string }> = {
    user: { name: t('accessDenied.user', 'User'), nameEn: 'User' },
    admin: { name: t('accessDenied.admin'), nameEn: 'Admin' },
  };

  const currentRole = user?.role || 'user';
  const currentRoleLabel = roleLabels[currentRole] || roleLabels.user;
  const requiredRoleLabel = roleLabels[requiredRole] || roleLabels.admin;

  // Determine the appropriate title and message based on denial reason
  const isOwnershipIssue = reason === 'ownership' ||
    (reason === 'unknown' && currentRole === requiredRole);

  const displayTitle = title || (isOwnershipIssue ? t('accessDenied.cannotAccess') : t('accessDenied.creatorRequired'));

  const getDefaultMessage = () => {
    if (!isAuthenticated || !user) {
      return t('accessDenied.loginRequired');
    }

    if (isOwnershipIssue) {
      return t('accessDenied.ownershipError');
    }

    return t('accessDenied.roleMessage', {
      role: currentRoleLabel.name,
      roleEn: currentRoleLabel.nameEn,
      requiredRole: requiredRoleLabel.name,
    });
  };

  const displayMessage = message || getDefaultMessage();

  return (
    <div className="access-denied">
      <div className="access-denied-icon">{isOwnershipIssue ? '🚫' : '🔒'}</div>
      <h2>{displayTitle}</h2>
      <p className="access-denied-message">
        {displayMessage}
        {isAuthenticated && user && !isOwnershipIssue && currentRole !== requiredRole && (
          <>
            <br />
            {t('accessDenied.switchRole', { role: requiredRoleLabel.name })}
          </>
        )}
      </p>

      {!isOwnershipIssue && (
        <div className="access-denied-info">
          <div className="info-card">
            <span className="info-icon">📚</span>
            <div>
              <strong>{t('accessDenied.learnerTitle')}</strong>
              <p>{t('accessDenied.learnerDesc')}</p>
            </div>
          </div>
          <div className="info-card">
            <span className="info-icon">✏️</span>
            <div>
              <strong>{t('accessDenied.creatorTitle')}</strong>
              <p>{t('accessDenied.creatorDesc')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="access-denied-actions">
        {isOwnershipIssue ? (
          <>
            <button
              className="btn-primary"
              onClick={() => navigate('/creator/drafts')}
            >
              {t('accessDenied.backToDrafts')}
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/creator/wizard')}
            >
              {t('accessDenied.createNewCourse')}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn-primary"
              onClick={() => navigate('/courses')}
            >
              {t('accessDenied.browseCourses')}
            </button>
            {isAuthenticated ? (
              <button
                className="btn-secondary"
                onClick={() => navigate('/profile')}
              >
                {t('accessDenied.goToProfile')}
              </button>
            ) : (
              <button
                className="btn-secondary"
                onClick={() => navigate('/login')}
              >
                {t('accessDenied.loginRegister')}
              </button>
            )}
          </>
        )}
      </div>

      {isAuthenticated && user?.role === 'user' && !isOwnershipIssue && (
        <p className="access-denied-hint">
          {t('accessDenied.upgradeHint')}
        </p>
      )}
    </div>
  );
};

export default AccessDenied;
